/**
 * Commercial Lifecycle console — Quotations · Rate Cards · Customer POs.
 * Minimal but production-shaped: list + create + revise/PDF + acceptance flow.
 */
import { orgLabel } from "@/lib/orgLabel";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, ListChecks, ClipboardList, Plus, RefreshCw, FileDown, Send, CheckCircle2, XCircle, RotateCcw, ArrowRight } from "lucide-react";
import RecordConversation from "@/components/app/RecordConversation";

const TABS = [
  { key: "quotations", label: "Quotations",  icon: FileText },
  { key: "rate_cards", label: "Rate cards",  icon: ListChecks },
  { key: "pos",        label: "Customer POs", icon: ClipboardList },
];

export default function CommercialConsole() {
  const [tab, setTab] = useState("quotations");
  const [quoteKpi, setQuoteKpi] = useState({ submitted: 0, approved: 0, pipeline_inr: 0, total: 0 });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/catalogue-cart/quotes");
        const items = data.items || [];
        setQuoteKpi({
          total: items.length,
          submitted: items.filter((q) => q.status === "SUBMITTED").length,
          approved: items.filter((q) => q.status === "APPROVED").length,
          pipeline_inr: items
            .filter((q) => ["SUBMITTED", "UNDER_REVIEW", "APPROVED"].includes(q.status))
            .reduce((s, q) => s + (q.grand_total_inr || q.subtotal_inr || 0), 0),
        });
      } catch { /* silent */ }
    })();
  }, []);

  return (
    <div className="max-w-6xl space-y-6" data-testid="commercial-console">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Commercial</div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Commercial lifecycle</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            One connected commercial journey — from customer-specific rate cards to quotations, customer POs and orders.
            Every rate card is versioned; historical transactions never lose their price.
          </p>
        </div>
        <RecordConversation contextType="general" contextLabel="Commercial · Invoicing" />
      </div>

      <Link to="/commercial/crm"
        data-testid="commercial-crm-workspace"
        className="block bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-md p-4 hover:border-emerald-400 hover:shadow-sm transition-all">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-700 font-semibold">
              Internal Sales CRM
            </div>
            <h3 className="text-lg font-heading font-semibold text-slate-900 mt-1">Commercial Workspace</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">Native Quotations, Customer POs & Vendor POs.</p>
          </div>
          <ArrowRight className="h-5 w-5 text-emerald-700" />
        </div>
      </Link>

      <Link to="/commercial/quotes"
        data-testid="commercial-quote-kpi"
        className="block bg-white border border-slate-200 rounded-md p-4 hover:border-slate-400 hover:shadow-sm transition-all">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Catalogue Quotes</div>
            <h3 className="text-lg font-heading font-semibold text-slate-900 mt-1">Quote Admin Console</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">Price, discount, approve incoming cart-to-quote requests.</p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Submitted</div>
              <div className={"text-2xl font-heading font-semibold mt-1 " +
                (quoteKpi.submitted > 0 ? "text-amber-700" : "text-slate-400")}>
                {quoteKpi.submitted}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Approved</div>
              <div className="text-2xl font-heading font-semibold text-emerald-700 mt-1">{quoteKpi.approved}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Pipeline</div>
              <div className="text-2xl font-heading font-semibold text-slate-900 mt-1">
                ₹{Math.round(quoteKpi.pipeline_inr).toLocaleString("en-IN")}
              </div>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-slate-400" />
        </div>
      </Link>

      <div className="border-b border-slate-200 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`commercial-tab-${t.key}`}
            className={
              "px-3 h-9 text-sm rounded-t-md flex items-center gap-2 border-b-2 -mb-px transition-colors " +
              (tab === t.key
                ? "border-slate-900 text-slate-900 font-medium bg-slate-50"
                : "border-transparent text-slate-500 hover:text-slate-900")
            }>
            <t.icon className="h-4 w-4" strokeWidth={1.6} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "quotations" && <QuotationsTab />}
      {tab === "rate_cards" && <RateCardsTab />}
      {tab === "pos"        && <POsTab />}
    </div>
  );
}

/* ------------- Quotations ------------- */
function QuotationsTab() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/quotations").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1" data-testid="quo-new">
          <Plus className="h-4 w-4" /> New quotation
        </button>
      </div>
      {showForm && <QuotationForm onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No quotations yet.</div>
        ) : items.map((q) => <QuotationRow key={q.id} q={q} onChanged={load} />)}
      </div>
    </section>
  );
}

function QuotationRow({ q, onChanged }) {
  const send = async () => {
    await api.post(`/quotations/${q.id}/send`);
    toast.success("Sent");
    onChanged();
  };
  const accept = async () => {
    await api.post(`/quotations/${q.id}/customer-action`, { action: "accept" });
    toast.success("Marked accepted");
    onChanged();
  };
  const reject = async () => {
    await api.post(`/quotations/${q.id}/customer-action`, { action: "reject" });
    toast.success("Marked rejected");
    onChanged();
  };
  const pdf = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/${q.id}/pdf`, "_blank");

  return (
    <div className="p-4 flex items-start gap-4" data-testid={`quo-row-${q.quotation_number}`}>
      <div>
        <div className="text-sm font-semibold text-slate-900 font-mono">{q.quotation_number}</div>
        <div className="text-xs text-slate-500 mt-0.5">v{q.version} · {q.created_at?.slice(0, 10)}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={q.status} />
          <span className="text-sm text-slate-800">
            {(q.lines || []).length} line{q.lines?.length === 1 ? "" : "s"} · <span className="font-medium">{q.grand_total?.toFixed?.(2)} {q.currency}</span>
          </span>
        </div>
        {q.notes && <div className="text-xs text-slate-600 mt-1">{q.notes}</div>}
      </div>
      <div className="shrink-0 flex flex-wrap gap-1">
        <button onClick={pdf} className="h-8 px-2 text-xs rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`quo-pdf-${q.quotation_number}`}><FileDown className="h-3 w-3" /> PDF</button>
        {q.status === "draft" && <button onClick={send} className="h-8 px-2 text-xs rounded-md bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1" data-testid={`quo-send-${q.quotation_number}`}><Send className="h-3 w-3" /> Send</button>}
        {["sent", "viewed", "revised", "customer_clarification"].includes(q.status) && (
          <>
            <button onClick={accept} className="h-8 px-2 text-xs rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Accept</button>
            <button onClick={reject} className="h-8 px-2 text-xs rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> Reject</button>
          </>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    draft:                 "text-slate-600 bg-slate-50 border-slate-200",
    internal_approval:     "text-amber-700 bg-amber-50 border-amber-200",
    sent:                  "text-sky-700 bg-sky-50 border-sky-200",
    viewed:                "text-sky-700 bg-sky-50 border-sky-200",
    customer_clarification:"text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
    revised:               "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
    accepted:              "text-emerald-700 bg-emerald-50 border-emerald-200",
    rejected:              "text-rose-700 bg-rose-50 border-rose-200",
    expired:               "text-slate-500 bg-slate-50 border-slate-200",
    cancelled:             "text-slate-500 bg-slate-50 border-slate-200",
  };
  return (
    <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (styles[status] || styles.draft)}>
      {status?.replace("_", " ")}
    </span>
  );
}

function QuotationForm({ onCancel, onCreated }) {
  const [masters, setMasters] = useState([]);
  const [f, setF] = useState({
    master_company_id: "", country: "IN", currency: "INR", valid_until: "", notes: "",
    lines: [{ description: "Device Deployment", service_code: "SVC-DEPLOY", quantity: 10, unit_price: 1250, discount_pct: 0, tax_rate: 18 }],
  });
  useEffect(() => { api.get("/master-companies").then(({ data }) => setMasters(data.items || [])); }, []);

  const totals = f.lines.reduce((acc, ln) => {
    const base = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
    const net = base - base * Number(ln.discount_pct || 0) / 100;
    const tax = net * Number(ln.tax_rate || 0) / 100;
    acc.subtotal += net; acc.tax += tax;
    return acc;
  }, { subtotal: 0, tax: 0 });

  const submit = async () => {
    if (!f.master_company_id) return toast.error("Choose customer");
    try {
      await api.post("/quotations", f);
      toast.success("Quotation drafted");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateLine = (i, patch) => {
    const next = [...f.lines];
    next[i] = { ...next[i], ...patch };
    setF({ ...f, lines: next });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-4" data-testid="quo-form">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <label className="md:col-span-2">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Customer</div>
          <select className="input" value={f.master_company_id} onChange={(e) => setF({ ...f, master_company_id: e.target.value })} data-testid="quo-customer">
            <option value="">—</option>
            {masters.map((m) => <option key={m.id} value={m.id}>{orgLabel(m, masters)}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Currency</div>
          <select className="input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
            {["INR", "USD", "EUR", "GBP", "SGD", "AED"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Valid until</div>
          <input type="date" className="input" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} />
        </label>
      </div>

      <div className="border border-slate-100 rounded-md">
        <div className="grid grid-cols-[3fr_0.6fr_0.9fr_0.6fr_0.6fr_0.9fr_0.4fr] gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 px-3 py-2 border-b border-slate-100 bg-slate-50">
          <div>Description</div><div>Qty</div><div>Unit price</div><div>Disc %</div><div>Tax %</div><div>Line total</div><div></div>
        </div>
        {f.lines.map((ln, i) => {
          const base = Number(ln.quantity || 0) * Number(ln.unit_price || 0);
          const net = base - base * Number(ln.discount_pct || 0) / 100;
          const tax = net * Number(ln.tax_rate || 0) / 100;
          return (
            <div key={i} className="grid grid-cols-[3fr_0.6fr_0.9fr_0.6fr_0.6fr_0.9fr_0.4fr] gap-2 px-3 py-2 items-center border-b border-slate-50">
              <input className="input h-8" value={ln.description} onChange={(e) => updateLine(i, { description: e.target.value })} />
              <input className="input h-8" type="number" value={ln.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} />
              <input className="input h-8" type="number" value={ln.unit_price} onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) })} />
              <input className="input h-8" type="number" value={ln.discount_pct} onChange={(e) => updateLine(i, { discount_pct: Number(e.target.value) })} />
              <input className="input h-8" type="number" value={ln.tax_rate} onChange={(e) => updateLine(i, { tax_rate: Number(e.target.value) })} />
              <div className="text-sm text-right tabular-nums text-slate-800">{(net + tax).toFixed(2)}</div>
              <button onClick={() => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) })} className="text-xs text-slate-500 hover:text-rose-700">Remove</button>
            </div>
          );
        })}
        <div className="p-2">
          <button onClick={() => setF({ ...f, lines: [...f.lines, { description: "", quantity: 1, unit_price: 0, discount_pct: 0, tax_rate: 18 }] })} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Add line</button>
        </div>
      </div>

      <div className="flex justify-end gap-6 text-sm">
        <div className="text-slate-500">Subtotal <span className="ml-1 font-medium text-slate-900 tabular-nums">{totals.subtotal.toFixed(2)}</span></div>
        <div className="text-slate-500">Tax <span className="ml-1 font-medium text-slate-900 tabular-nums">{totals.tax.toFixed(2)}</span></div>
        <div className="text-slate-800 font-heading">Grand total <span className="ml-1 text-slate-900 font-semibold tabular-nums">{(totals.subtotal + totals.tax).toFixed(2)} {f.currency}</span></div>
      </div>

      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="h-9 px-3 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
        <button onClick={submit} className="h-9 px-3 text-sm bg-slate-900 text-white hover:bg-slate-800 rounded-md" data-testid="quo-save">Save draft</button>
      </div>
    </div>
  );
}

/* ------------- Rate Cards ------------- */
function RateCardsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/rate-cards").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);
  return (
    <section className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No rate cards yet. Create one via POST /api/rate-cards then add lines.</div>
        ) : items.map((rc) => (
          <div key={rc.id} className="p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900">{rc.name} <span className="text-xs text-slate-500 font-mono">v{rc.version}</span></div>
              <div className="text-xs text-slate-500 mt-0.5">
                {rc.master_company_id ? "Customer-specific" : (rc.country ? `Country default (${rc.country})` : "Global default")}
                {" · "}{rc.currency}
                {" · "}{rc.effective_from?.slice(0, 10) || "—"} → {rc.effective_to?.slice(0, 10) || "open"}
              </div>
            </div>
            <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (rc.status === "active" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-slate-500 bg-slate-50 border-slate-200")}>{rc.status}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ------------- Customer POs ------------- */
function POsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/customer-po").then(({ data }) => setItems(data.items || [])); }, []);
  return (
    <section className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {items.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No customer POs yet.</div>
        ) : items.map((po) => (
          <div key={po.id} className="p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (po.match_status === "matched" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200")}>{po.match_status?.replace("_"," ")}</span>
              <div className="text-sm font-semibold text-slate-900 font-mono">{po.po_number}</div>
              <div className="text-xs text-slate-500">for {po.quotation_number} · {po.po_value?.toFixed?.(2)} {po.currency} · {po.po_date}</div>
            </div>
            {po.match_reasons?.length > 0 && (
              <ul className="text-xs text-amber-800 mt-1 list-disc pl-5">
                {po.match_reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
